import React, { useState, useEffect, useRef } from "react";
import { Navigate, useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { trackDestinationUpdated } from '../../analytics/analytics';
import DestinationDetailsModal from "./DestinationDetailsModal";
import NotFoundPage from "../common/NotFoundPage";
import { ConnectionNotFoundFallback } from "./ConnectionNotFoundFallback";
import { providerRouteRedirectTarget } from "./providerRouteRedirect";
import { isValidRouteSubpage } from "../routes/routeSubpages";
import { Route, ConnectionConfiguration, Destination, Organization, buildDestination } from "../common/ObjectTypes";
import { addRouteDestinations, createRoute, getRoutesList, getRoutesByIntegration, listRoutes, testConnectionAction } from "../../api/routes/routesApi";
import { routeRecordId } from "../routes/routeDisplayName";
import { validateBaseUrl } from "./validateBaseUrl";
import AddToRouteDrawer from "./AddToRouteDrawer";
import ModalShell from "../common/ModalShell";
import UnsavedSectionPrompt, { PendingEdits, SectionPrompt } from "../common/configPanel/UnsavedSectionPrompt";
import { buttonClasses } from "../common/tailwindClassnames";
import { getDestinationDetails, updateDestination } from "../../api/destinations/destinationsApi";
import { toast } from "react-toastify";
import { loadOrganizations } from "../../api/organizations/loadOrganizations";
import { filter_page_size, page_size } from "../../api/apiUtils";
import { useParams, useLocation } from "react-router-dom";
import { useKeycloakToken } from "../common/useKeycloak";
import { useTranslation } from "react-i18next";
import { baseUrlIsRequired } from "../routes/providerBaseUrl";
import { isEarthRangerDestinationType, extractEarthRangerSiteName, constructEarthRangerUrl } from "./earthRangerUrl";
import { loadCurrentUserDetails } from "../organizations/loadCurrentUserDetails";

export function ManageDestinationDetails() {
    const { keycloak } = useKeycloak();
    const { t } = useTranslation();
    const [token] = useKeycloakToken();
    const { dest_id, subpage } = useParams();
    const location = useLocation();
    // Determine subpage from the URL path. The Routes tab carries an explicit
    // /routes suffix so the bare /connections/:id path — which the provider→route
    // redirect below claims — is never the only way to reach it.
    // Normalized first: the router matches /connections/:id/routes/ (trailing
    // slash) too, and a raw endsWith test would hand that URL the wrong tab.
    const pathname = location.pathname.replace(/\/+$/, "") || "/";
    const isConfig = pathname.endsWith('/configuration');
    const isCanonicalPath = pathname.startsWith('/connections/');
    // Exactly the bare canonical path opens Configuration: for a non-provider
    // record it is a resting place, and Configuration is what every in-app
    // link opens. Everything else — the /routes suffix and the non-canonical
    // mounts (/sources/destination/:id, /routes/:rid/:subpage/:id) — shows the
    // Routes tables.
    const isBareConnection = !!dest_id && pathname === `/connections/${dest_id}`;
    const currentSubPage = (isConfig || isBareConnection) ? 'configuration' : 'details';
    const navigate = useNavigate();
    const [currentMemberIsAdmin, setCurrentMemberIsAdmin] = useState<boolean>(false);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [destination, setDestination] = useState<Destination>(buildDestination())
    const [destinationTitle, setDestinationTitle] = useState<string>("")
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [orderingString, setOrderingString] = useState("");
    const [loadingRoutes, setLoadingRoutes] = useState(true);
    const [destRoutesFailed, setDestRoutesFailed] = useState(false);
    const [selectedDestRoutes, setSelectedDestRoutes] = useState<Route[]>([]);
    const [changesMade, setChangesMade] = useState(false);
    // What the configuration panel currently has unsaved. The tabs and the links
    // out of this page are here, not in the panel, so they ask it before moving.
    const pendingEditsRef = useRef<PendingEdits | null>(null);
    const [sectionPrompt, setSectionPrompt] = useState<SectionPrompt | null>(null);
    // Any navigation that would unmount the configuration panel and take its
    // unsaved edits with it. Routed through the same Save-or-discard question the
    // panel asks when you switch sections inside it.
    const guardedGo = (go: () => void) => {
        const pending = pendingEditsRef.current;
        if (!pending?.hasEdits()) return go();
        setSectionPrompt({
            onSave: () => pending.save(),
            onDiscard: () => pending.discard(),
            proceed: go,
        });
    };
    // The provider-role answer carries the dest_id it describes, so an answer is
    // never read as if it were about a different record. Storing a bare boolean
    // instead leaves one committed frame — after dest_id changes but before this
    // effect re-runs — where the previous record's answer decides Create Route
    // for the new one, which is how duplicates (and orphans — GUNDI-5428) get made.
    const [providerRole, setProviderRole] = useState<{ destId: string, routes: any[], failed: boolean } | null>(null);
    const providerRoleRequestId = useRef(0);
    // Derived, so switching records reverts to "unknown" in the same render that
    // changes dest_id rather than one effect later.
    const providerRoleLoaded = !!dest_id && providerRole?.destId === dest_id;
    // The details answer, keyed to the URL the same way providerRole is. NOT
    // about stale records from a previous id — a dest_id change remounts this
    // component (key={dest_id} in App.tsx), resetting `destination`. What this
    // marks is the window where `destination` is still the empty placeholder
    // (details pending, failed, or the record came back without an owner):
    // role ACTIONS must not be offered then, because they would create or
    // attach with an empty id and owner.
    const destinationLoaded = !!dest_id && destination.id === dest_id;
    // Every Route this record provides data to (a Connection can feed many).
    const providerRoutes: any[] = providerRoleLoaded ? providerRole!.routes : [];
    // The lookup answered, but with a failure: "no Routes" is unknown, so the
    // section says so instead of offering to create a duplicate.
    const providerRoleFailed = providerRoleLoaded ? providerRole!.failed : false;
    const [confirmDataSource, setConfirmDataSource] = useState(false);
    const [creatingDataSource, setCreatingDataSource] = useState(false);
    // "Add to a Route": pick an existing Route and this record joins its
    // destinations. Candidates come from the same Routes list the portal
    // already uses, defaulting to the record's workspace.
    const [routeDrawerOpen, setRouteDrawerOpen] = useState(false);
    const [routeCandidates, setRouteCandidates] = useState<any[]>([]);
    const [loadingCandidates, setLoadingCandidates] = useState(false);
    const [candidatesHaveMore, setCandidatesHaveMore] = useState(false);
    const [drawerWorkspaceFilter, setDrawerWorkspaceFilter] = useState<{ label: string, value: string } | null>(null);
    const [drawerSearch, setDrawerSearch] = useState("");
    const [testActionLoading, setTestActionLoading] = useState(false);
    const [testActionData, setTestActionData] = useState<any>(null);
    const [errors, setErrors] = useState({});
    const [totalCount, setTotalCount] = useState<number>(0);
    const [offset, setOffset] = useState<number>(0);
    const [orgSearchInput, setOrgSearchInput] = useState<string>("");
    const [reloadDestination, setReloadDestination] = useState<boolean>(false);
    // Callers that need to KNOW when the reload finished (the configuration
    // editor, which drops its unsaved-edit stash only once the fetched record
    // carries the saved values — otherwise the form flashes the pre-save value).
    // The flag alone cannot tell them: setting it returns immediately.
    const pendingReloadWaiters = useRef<(() => void)[]>([]);
    const isEarthRanger = isEarthRangerDestinationType(destination.type.name);
    // Ref for scrollable content
    const scrollableContentRef = useRef<HTMLDivElement | null>(null);

    // Closing the configuration screen unmounts the panel, so it asks first for
    // the same reason a tab switch does.
    const onToggleModal = () => guardedGo(() => {
        // Canonical routes: /connections/:dest_id[/configuration|/routes].
        // Close always goes to the list — going "up" to the bare path instead
        // would either re-open Configuration (the bare default) or, for a
        // provider record, bounce to its route.
        if (isCanonicalPath) {
            navigate('/connections');
            return;
        }

        // Legacy routes: /destination/:dest_id and nested /.../destination/:dest_id
        const pathArray = pathname.split('/').filter(Boolean);
        const destinationIndex = pathArray.indexOf('destination');
        if (destinationIndex > -1) {
            const newPathArray = pathArray.slice(0, destinationIndex);
            const newPath = `/${newPathArray.join('/')}` || '/';
            navigate(newPath);
        }
    });

    const loadOrganizationsData = async (search_str: string = "") => {
        const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
        if (!isLoggedIn || !keycloak.token) return;
        try {
            if (search_str) {
                const [orgs, , error] = await loadOrganizations(keycloak.token, 0, search_str, []);
                if (!error) setOrganizations(orgs);
            } else {
                let accumulated: Organization[] = [];
                let offset = 0;
                while (true) {
                    const [orgs, total, error] = await loadOrganizations(keycloak.token, offset, "", accumulated);
                    if (error) break;
                    accumulated = orgs;
                    if (offset + filter_page_size >= total) break;
                    offset += filter_page_size;
                }
                setOrganizations(accumulated);
            }
        } catch (err) {
            console.log('Failed to load organizations:', err);
        }
    };

    useEffect(() => {
        // An invalid :subpage renders the 404 page — skip the fetch cascade
        if (!isValidRouteSubpage(subpage)) return;
        const loadSelectedDestinationRoutes = async () => {
            // Check if user is authenticated before making API calls
            const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
            if (!isLoggedIn) {
                return;
            }

            if (keycloak.token) {
                try {
                    // Route records, not connections: each row is one route with
                    // its own URL, decoupled from the provider integration.
                    const temp_routes = await getRoutesByIntegration(keycloak.token, "destination", dest_id || "", {
                        ordering: orderingString,
                        offset: offset,
                    });
                    setSelectedDestRoutes(temp_routes.results);
                    setTotalCount(temp_routes.count);
                    // Same distinction as the provider role: a failed lookup is not
                    // "this Connection receives from nothing".
                    setDestRoutesFailed(!temp_routes.ok);
                    setLoadingRoutes(false);
                    setPageLoaded(true);
                } catch (err) {
                    console.log(err)
                }
            }
        };

        const loadProviderRole = async () => {
            if (!keycloak.token || !dest_id) return;
            const requestedFor = dest_id;
            const request = ++providerRoleRequestId.current;
            // A Connection has a single API key, so it can only source one Route.
            // `failed` keeps a failed lookup distinct from a successful "no routes":
            // treating the two alike offers Create Route for a provider that already
            // has one, which is how duplicates (and orphans — GUNDI-5428) get made.
            const data = await getRoutesByIntegration(keycloak.token, "provider", requestedFor);
            // Drop a response a newer lookup has superseded — without this an older,
            // slower request can land last and answer for the record the user left.
            if (request !== providerRoleRequestId.current) return;
            setProviderRole({ destId: requestedFor, routes: data.results, failed: !data.ok });
        };
        loadProviderRole();

        const loadDestinationDetails = async () => {
            // Check if user is authenticated before making API calls
            const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
            if (!isLoggedIn || !keycloak.token || !dest_id) {
                // No request to make, so the reload request is over: clear the flag
                // rather than leave it latched at true (see the finally below).
                if (reloadDestination) setReloadDestination(false);
                return;
            }

            {
                try {
                    const temp = await getDestinationDetails(keycloak.token, dest_id);
                    // Check if temp is defined and has required properties
                    if (!temp || !temp.owner || !temp.owner.id) {
                        return;
                    }
                    const temp_user = await loadCurrentUserDetails(temp.owner.id, keycloak.token);
                    setCurrentMemberIsAdmin(temp_user);
                    loadSelectedDestinationRoutes();
                    // Use temp.type.name directly — the component-level isEarthRanger is stale here
                    // since destination.type.name is "" until this effect sets it.
                    if (isEarthRangerDestinationType(temp.type.name)) {
                        setDestination({ ...temp, base_url: extractEarthRangerSiteName(temp.base_url) });
                    } else {
                        setDestination(temp);
                    }
                    setDestinationTitle(temp.name);
                    // Reset only on success (not at the top of the try): an eager reset
                    // flashes the loading state over the 404 page while a retry runs
                    setNotFound(false);
                } catch (err: any) {
                    // 403 is treated like 404: the entity is not viewable by this user
                    if (err?.response?.status === 404 || err?.response?.status === 403) {
                        setNotFound(true);
                        return;
                    }
                    // Silently handle errors - they're already handled by the interceptor
                    console.log('Failed to load destination details:', err);
                } finally {
                    // On EVERY path, including the early returns above and a failed
                    // request: leaving the flag set meant the next reload request
                    // (setReloadDestination(true) on an already-true flag) never
                    // re-ran this effect, and anyone awaiting the reload waited
                    // forever.
                    if (reloadDestination) setReloadDestination(false);
                }
            }
        };

        loadDestinationDetails();
        if (!pageLoaded) loadOrganizationsData(); // load once on mount; orgs are stable for the session
    }, [reloadDestination, dest_id, keycloak.token, offset, orderingString, subpage]);

    // The reload flag is cleared by the fetch above once the record is in state,
    // so this fires on the first render that already shows the fresh data.
    useEffect(() => {
        if (reloadDestination || pendingReloadWaiters.current.length === 0) return;
        const waiters = pendingReloadWaiters.current;
        pendingReloadWaiters.current = [];
        waiters.forEach((resolve) => resolve());
    }, [reloadDestination, destination]);

    const reloadDestinationAndWait = () => new Promise<void>((resolve) => {
        pendingReloadWaiters.current.push(resolve);
        setChangesMade(false);
        setReloadDestination(true);
    });

    const candidateRequestId = useRef(0);
    const loadRouteCandidates = async (search: string, workspace: { label: string, value: string } | null) => {
        if (!keycloak.token) return;
        const request = ++candidateRequestId.current;
        setLoadingCandidates(true);
        try {
            // Route RECORDS, not connections: a connection entry describes the
            // provider and carries the UNION of destinations across all its Routes,
            // so filtering on it hid eligible Routes and attaching used whichever
            // Route happened to be first. A record knows its own destinations.
            const data = await listRoutes(keycloak.token, { search, ordering: "name" });
            // The routes endpoint has no owner filter, so the workspace narrowing
            // happens here on the page that was fetched.
            const inWorkspace = workspace
                ? data.results.filter((r: any) => r?.owner === workspace.value || r?.owner?.id === workspace.value)
                : data.results;
            // A Route can't receive data from its own provider, and re-adding a
            // destination it already has would be a no-op — hide both.
            const eligible = inWorkspace.filter((r: any) =>
                !(r?.data_providers || []).some((p: any) => p?.id === dest_id)
                && !(r?.destinations || []).some((d: any) => d?.id === dest_id));
            if (request !== candidateRequestId.current) return;
            setRouteCandidates(eligible);
            setCandidatesHaveMore(!!data.next);
        } catch (err) {
            console.log(err);
        } finally {
            if (request === candidateRequestId.current) setLoadingCandidates(false);
        }
    };

    const openRouteDrawer = () => {
        const ws = destination.owner?.id ? { label: destination.owner.name, value: destination.owner.id } : null;
        setDrawerWorkspaceFilter(ws);
        setDrawerSearch("");
        setRouteDrawerOpen(true);
        loadRouteCandidates("", ws);
    };

    // `route` here is a route RECORD from the picker, so its id is the id to PATCH
    // and its destinations are its own.
    const handleAttachToRoute = async (route: any) => {
        if (!keycloak.token) return;
        const route_id = route?.id;
        if (!route_id) {
            console.log("cannot attach: candidate has no route id", route);
            toast.error(t("Something went wrong. Please try again or contact support."), { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        try {
            const existing = (route.destinations || []).map((d: any) => d.id);
            const updated = await addRouteDestinations(keycloak.token, route_id, {
                destinations: Array.from(new Set([...existing, dest_id])) as string[],
            });
            // The API layer swallows a 401 and resolves undefined; the login modal is
            // already up, so say nothing rather than claiming the record was attached.
            if (!updated) return;
            toast.success(t("Connection updated"), { position: toast.POSITION.TOP_RIGHT });
            setRouteDrawerOpen(false);
            navigate(`/routes/${route_id}/configurations/account/${dest_id}`);
        } catch (err: any) {
            console.log(err);
            if (err?.response?.status !== 401) {
                toast.error(t("Something went wrong. Please try again or contact support."), { position: toast.POSITION.TOP_RIGHT });
            }
        }
    };

    // Capability decides which roles are on offer: pull to be a source, push to be
    // a destination. A pull-only technology cannot receive data, so attaching it to a
    // Route would produce a destination that can never work.
    const canBeDataSource = destinationLoaded && (destination.type?.actions || []).some((a: any) => a.type === "pull");
    const canBeDestination = destinationLoaded && (destination.type?.actions || []).some((a: any) => a.type === "push");

    // Reuses this very record as the Route's provider: one account, two roles,
    // one set of credentials. Destinations are chosen afterwards, on the flow map.
    // The new Route gets its own name — prefilled with the Connection's for
    // convenience, but decoupled: the user can (and often should) change it.
    const [dataSourceRouteName, setDataSourceRouteName] = useState("");
    const handleUseAsDataSource = async () => {
        if (!keycloak.token || creatingDataSource) return;
        // Backstop for the destinationLoaded gate: never create a Route from
        // the empty placeholder record.
        if (!destinationLoaded) return;
        const ownerId = destination.owner?.id;
        if (!ownerId) {
            toast.error(t("Something went wrong. Please try again or contact support."), { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        setCreatingDataSource(true);
        try {
            const created = await createRoute(keycloak.token, {
                name: dataSourceRouteName.trim() || destination.name,
                owner: ownerId,
                data_providers: [destination.id],
                destinations: [],
            });
            setConfirmDataSource(false);
            navigate(`/routes/${created.id}/configurations`, { state: { highlightAddDestination: true } });
        } catch (err: any) {
            console.log(err);
            if (err?.response?.status !== 401) {
                toast.error(t("Something went wrong. Please try again or contact support."), { position: toast.POSITION.TOP_RIGHT });
            }
        } finally {
            setCreatingDataSource(false);
        }
    };

    const handlePageClick = (e: any) => {
        setOffset(e.selected * page_size);
        setLoadingRoutes(true);
    }

    const requestSortSelectedDestinationRoute = (orderBy: string, sortDirection: boolean) => {
        const ordering = sortDirection ? orderBy : "-" + orderBy;
        setOrderingString(ordering);
        setLoadingRoutes(true);
    }

    const onChangeSelectedDestination = (e: any) => {
        const attr: string = e.target.id
        setChangesMade(true);
        setDestination((prevDestination: Destination) => ({
            ...prevDestination,
            [attr]: e.target.value
        }))
    }

    const onChangeSelectedDestinationOwner = (e: any) => {
        setChangesMade(true);
        setDestination((prevDestination: Destination) => ({
            ...prevDestination,
            "owner": {
                name: e.label,
                id: e.value,
                description: ""
            },
        }))
    }

    const handleTestAction = async (action_id: string, action_type: string) => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setTestActionLoading(true);
            // The stored configuration for this action — sections are saved before
            // they can be tested, so there is no unsaved copy to prefer.
            const stored = destination.configurations.find((a: ConnectionConfiguration) => a.action.id === action_id);
            const form_data = stored?.data ?? {};
            try {
                let result = await testConnectionAction(token, destination.id, action_type, { config_overrides: Object(form_data) });
                if (result) {
                    setTestActionData(result.data);
                }
            } catch (err: any) {
                setTestActionData({ error: err?.response?.data?.detail || err?.message || "Server error. Please try again." });
            } finally {
                setTestActionLoading(false);
            }
        }
    }

    const formIsValid = () => {
        let form_errors: any = {};
        const shouldRequireUrl = baseUrlIsRequired(destination.type.value);
        const urlError = validateBaseUrl({
            value: destination.base_url, asSiteName: isEarthRanger, optional: !shouldRequireUrl, t,
        });
        if (urlError) form_errors.base_url = urlError;
        if (destination.name.length === 0) {
            form_errors.name = t("Name is required.");
        }
        setErrors(form_errors);
        return Object.keys(form_errors).length === 0;
    }

    // action: 'save' | 'saveAndClose'
    // Resolves true only when the save landed — the section-switch prompt uses it
    // to decide whether it is safe to move to the section the user asked for.
    const handleSaveDestination = async (e: React.FormEvent<Element>): Promise<boolean> => {
        if (!formIsValid()) return false;
        else {
            if (keycloak.token) {
                try {
                    const saved = await updateDestination(keycloak.token, destination.id, {
                        "base_url": isEarthRanger ? constructEarthRangerUrl(destination.base_url) : destination.base_url,
                        "enabled": destination.enabled,
                        "owner": destination.owner.id,
                        "name": destination.name
                    });
                    // A 401 is swallowed by the API layer (the login modal handles
                    // it) and comes back as undefined — that is not a save, so the
                    // edits stay put rather than being cleared behind a toast.
                    if (!saved) return false;
                    toast.success(`${t("Destination updated")}`, {
                        position: toast.POSITION.TOP_RIGHT
                    });
                    trackDestinationUpdated(destination.id, destination.type?.name, destination.owner?.name);
                    setChangesMade(false);
                    setReloadDestination(true);
                    return true;
                } catch (err: any) {
                    toast.error(t("Failed to save destination. Please try again."), { position: toast.POSITION.TOP_RIGHT });
                    return false;
                }
            }
        }
        return false;
    }

    function handleSearchOrgDropdown(e: any) {
        setOrgSearchInput(e);
        if (e.length > 2) loadOrganizationsData(e);
        else if (e.length === 0) loadOrganizationsData();
    }

    function handleSelectSubPage(e: string) {
        if (!dest_id) return;
        guardedGo(() => {
            if (e === 'details') {
                // The Routes tab lives at /routes, NOT the bare path: the bare
                // path is claimed by the provider→route redirect, which used to
                // swallow this very tab click for provider records.
                navigate(`/connections/${dest_id}/routes`);
            } else {
                // Go to the subpage for this destination
                navigate(`/connections/${dest_id}/${e}`);
            }
        });
    }

    // The /routes/:route_id/:subpage/:dest_id(/configuration) routes
    // accept any :subpage string — validate it like ManageRouteDetails does
    if (!isValidRouteSubpage(subpage)) return <NotFoundPage />;
    // A missing integration under /connections/:id can be an old link carrying
    // a route id; every other mount point of this screen goes straight to 404.
    // startsWith (not an exact match) is deliberate: this branch only runs when
    // the integration lookup already 404'd, so on /connections/:id/configuration
    // there is no page to protect — the choice is the 404 page vs the route the
    // id names, and the "dropped" suffix pointed at a screen that never existed.
    // (Unlike providerRouteRedirect, which IS bare-path-only because there the
    // connection exists and /configuration must stay reachable.)
    if (notFound) return isCanonicalPath
        ? <ConnectionNotFoundFallback id={dest_id} />
        : <NotFoundPage />;

    // GUNDI-5639 (temporary): /connections/:id where the id provides data to a
    // route is an old route link — send it to the route. See
    // providerRouteRedirect.ts for why this is deliberately narrow.
    // Deliberately not gated on destinationLoaded: the redirect only reads the
    // role answer (which is keyed to dest_id), and waiting for the details
    // lookup would just delay old-link redirects.
    const providerRouteRedirect = providerRouteRedirectTarget(location.pathname, dest_id, {
        loaded: providerRoleLoaded,
        failed: providerRoleFailed,
        routes: providerRoutes,
    });
    if (providerRouteRedirect) return <Navigate to={providerRouteRedirect} replace />;

    return (
        <>
        <DestinationDetailsModal
            currentMemberIsAdmin={currentMemberIsAdmin}
            destination={destination}
            destinationTitle={destinationTitle}
            destSubPage={currentSubPage}
            pendingEditsRef={pendingEditsRef}
            onSelectSubPage={handleSelectSubPage}
            organizations={organizations}
            loadMoreOrgs={() => {}}
            orgInputChange={handleSearchOrgDropdown}
            routes={selectedDestRoutes}
            onToggleModal={onToggleModal}
            requestSort={requestSortSelectedDestinationRoute}
            loadingRoutes={loadingRoutes}
            onChangeSelectedDestination={onChangeSelectedDestination}
            onChangeSelectedDestinationOwner={onChangeSelectedDestinationOwner}
            onTestAction={handleTestAction}
            testActionLoading={testActionLoading}
            testActionResponse={testActionData}
            onSave={handleSaveDestination}
            onReloadDestination={() => { setChangesMade(false); setReloadDestination(true); }}
            onReloadDestinationAndWait={reloadDestinationAndWait}
            providerRoutes={providerRoutes}
            providerRoleFailed={providerRoleFailed}
            destRoutesFailed={destRoutesFailed}
            canBeDataSource={canBeDataSource}
            canBeDestination={canBeDestination}
            // Deliberately NOT && destinationLoaded: this flag drives the
            // skeleton/error/table display for role data that is keyed on its
            // own, so a stalled or failed details fetch must not hide the
            // "Couldn't load" message behind an eternal skeleton. The role
            // ACTIONS are gated separately via canBeDataSource/canBeDestination.
            providerRoleLoaded={providerRoleLoaded}
            onUseAsDataSource={() => { setDataSourceRouteName(destination.name); setConfirmDataSource(true); }}
            onAddToRoute={openRouteDrawer}
            changesMade={changesMade}
            errors={errors}
            onPageClicked={handlePageClick}
            totalCount={totalCount}
            pageLoaded={pageLoaded}
            take={page_size}
            offset={offset}
            scrollableContentRef={scrollableContentRef}
            authToken={token && typeof token === 'string' && !keycloak.isTokenExpired() ? token : undefined}
        />
        <UnsavedSectionPrompt prompt={sectionPrompt} onClose={() => setSectionPrompt(null)} />
        <AddToRouteDrawer
            isOpen={routeDrawerOpen}
            onClose={() => setRouteDrawerOpen(false)}
            candidates={routeCandidates}
            loading={loadingCandidates}
            onAttach={handleAttachToRoute}
            onSearch={(searchStr: string) => { setDrawerSearch(searchStr); loadRouteCandidates(searchStr, drawerWorkspaceFilter); }}
            hasMore={candidatesHaveMore}
            workspaceOptions={organizations.filter((o: Organization) => o.id).map((o: Organization) => ({ label: o.name, value: o.id as string }))}
            workspaceFilter={drawerWorkspaceFilter}
            onChangeWorkspaceFilter={(option) => { setDrawerWorkspaceFilter(option); loadRouteCandidates(drawerSearch, option); }}
        />
        {confirmDataSource && (
            <ModalShell
                title={t("Use {{name}} as a data source", { name: destination.name })}
                onClose={() => setConfirmDataSource(false)}
                footer={<>
                    <button type="button" className={buttonClasses.primary} disabled={creatingDataSource}
                        onClick={handleUseAsDataSource}>
                        {t("Create Route")}
                    </button>
                    <button type="button" className={buttonClasses.cancel} onClick={() => setConfirmDataSource(false)}>
                        {t("Cancel")}
                    </button>
                </>}>
                <p className="text-base text-off-black">
                    {t("This creates a Route with {{name}} as its source. The same account keeps its role as a destination and its credentials stay shared, and you choose where the data goes next.", { name: destination.name })}
                </p>
                <div className="mt-4 flex flex-col">
                    <label htmlFor="data_source_route_name" className="text-sm font-semibold text-off-black mb-1">{t("Route Name")}</label>
                    <input
                        id="data_source_route_name"
                        data-testid="ManageDestinationDetails-route-name-input"
                        value={dataSourceRouteName}
                        onChange={(e) => setDataSourceRouteName(e.target.value)}
                        className="w-full p-1.5 border rounded px-2 transition-colors duration-300 border-neutral-gray-200"
                    />
                </div>
            </ModalShell>
        )}
        </>
    )
}